"use client";

import VideoPage from "@12-week/components/VideoPage";
import VideoPlayerCard from "@12-week/components/VideoPlayerCard";
import type { TwelveWeekExercise } from "@12-week/lib/types";
import type { StreamPlayerApi } from "@cloudflare/stream-react";
import { Button } from "@packages/components/ui/Button";
import Card from "@packages/components/ui/Card";
import Heading from "@packages/components/ui/Heading";
import LinkButton from "@packages/components/ui/LinkButton";
import { LIST } from "@packages/design-system/constants";
import React from "react";

function SeeTimestampButton({
  timestampSeconds,
  onClick,
}: {
  timestampSeconds: number;
  onClick: (timestampSeconds: number) => void;
}) {
  return (
    <Button
      type="button"
      variant="noStyles"
      size="none"
      className={`text-link/80 hover:text-link-hover pl-1`}
      onClick={() => onClick(timestampSeconds)}
    >
      {Math.floor(timestampSeconds / 60)}:
      {String(timestampSeconds % 60).padStart(2, "0")}
    </Button>
  );
}

interface ExercisePageContentProps {
  exercise: TwelveWeekExercise;
  slug: string;
  fromProgram?: string;
  fromWeek?: string;
  pages: Array<{ name: string; href: string | null; current: boolean }>;
  hideInstructions?: boolean;
}

export default function ExercisePageContent({
  exercise,
  slug,
  fromProgram,
  fromWeek,
  pages,
  hideInstructions,
}: ExercisePageContentProps) {
  // Ref for the Cloudflare Stream player API
  const streamRef = React.useRef<StreamPlayerApi | undefined>(undefined);

  // Handler to seek and play video
  const handleSeeTimestamp = (timestamp: number) => {
    if (streamRef.current) {
      streamRef.current.currentTime = timestamp;
      streamRef.current.play();
    }
  };

  return (
    <VideoPage
      video={exercise}
      breadcrumbBasePath={pages[0]?.href || "/x3-exercises"}
      breadcrumbBaseName={pages[0]?.name || "X3 Exercises"}
      overline={pages[0]?.name || "X3 Exercises"}
      currentSlug={slug}
      program="exercises"
      fromProgram={fromProgram}
      fromWeek={fromWeek}
      streamRef={streamRef}
    >
      {/* Section: Initial Setup */}
      {!hideInstructions &&
        exercise.initialSetup &&
        exercise.initialSetup.length > 0 && (
          <Card as="section">
            <>
              <Heading level={2} heading="Initial Setup" />
              <ul className={LIST}>
                {exercise.initialSetup.map((setupPoint, idx) => (
                  <li key={idx}>
                    {setupPoint.label}
                    {setupPoint.timestampSeconds !== undefined && (
                      <SeeTimestampButton
                        timestampSeconds={setupPoint.timestampSeconds}
                        onClick={handleSeeTimestamp}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </>
          </Card>
        )}

      {/* Section: Execution & Mechanics */}
      {!hideInstructions &&
        exercise.formPoints &&
        exercise.formPoints.length > 0 && (
          <Card as="section">
            <>
              <Heading level={2} heading="Execution & Mechanics" />
              <ul className={LIST}>
                {exercise.formPoints.map((formPoint, idx) => (
                  <li key={idx}>
                    {formPoint.label}
                    {formPoint.timestampSeconds !== undefined && (
                      <SeeTimestampButton
                        timestampSeconds={formPoint.timestampSeconds}
                        onClick={handleSeeTimestamp}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </>
          </Card>
        )}

      {/* Section: Related Exercises (Variations) */}
      {exercise.variations && exercise.variations.length > 0 && (
        <Card as="section">
          <>
            <Heading level={2} heading="Related Exercises" headingSize={3} />
            <div className="flex flex-wrap gap-3">
              {exercise.variations.map((variationSlug) => (
                <LinkButton
                  key={variationSlug}
                  href={`/x3-exercises/${variationSlug}`}
                  variant="outline"
                  size="lg"
                >
                  {variationSlug
                    .replace(/-/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </LinkButton>
              ))}
            </div>
          </>
        </Card>
      )}

      {/* Section: FAQ Video */}
      {exercise.faqVideo && (
        <Card as="section">
          <>
            <Heading
              level={2}
              headingSize={3}
              heading={
                exercise.faqVideoTitle || "Have Questions About This Movement?"
              }
              subheading={
                exercise.faqVideoDescription ||
                "Watch this short video to learn more."
              }
            />
            <div className="mx-auto mt-4 max-w-md">
              <VideoPlayerCard video={exercise.faqVideo} as="section" />
            </div>
          </>
        </Card>
      )}
    </VideoPage>
  );
}
